import axios from "axios";
import {getProxyAccessToken} from "../../plugins/cookie-plugins"
import {proxyPath, proxyBody, proxyHeaders} from "../../utils/b2bProxy"
import {
    b2bAuthenticate,
    b2bValidateSession,
    b2bRefreshToken,
    b2bErrorCode,
    isTransientAxiosError,
    B2B_ERROR_TOKEN_EXPIRED,
    B2B_ERROR_NO_TOKEN
} from "@/services/b2bAuthService";
import {
    startB2bProxySession,
    updateB2bProxyTokens,
    getB2bProxyAccessToken,
    getB2bProxyRefreshToken,
    isB2bReauthDue,
    noteB2bProxyTokenRejected,
    noteB2bProxyTokenAccepted,
    getB2bProxyTokenRejections
} from "@/services/b2bProxySession";

// How often the partner's token is checked again with a new /b2b/authenticate (also a fresh proxy token).
// In between, the watcher only validates the proxy token, as the skins do.
export const B2B_REAUTH_INTERVAL_MS = 10 * 60 * 1000;

// Proxy tokens rejected in a row before giving up: the token of a re-authentication rejected again
// means re-authenticating does not help (proxy misconfigured, ...). Without the cap the watcher would
// open a Fusio session every minute for as long as the page stays open.
export const B2B_MAX_REJECTED_TOKENS = 2;

// One re-authentication at a time: a second caller waits for the one in flight.
let b2bReauthInFlight = null;

// /b2b/authenticate with the partner's token, a fresh proxy session on success. An expired partner
// token (6002) is refreshed once (rocky-bets /b2b/users/token), then authenticated again, never more.
async function authenticateB2bAgain({dispatch, rootGetters}, afterRefresh) {
    const operatorToken = rootGetters['user/getB2bToken'];
    if (!operatorToken) return {valid: false, code: B2B_ERROR_NO_TOKEN, transient: false};

    try {
        const auth = await b2bAuthenticate({proxyUrl: rootGetters['settings/getProxyUrl'], operatorToken});
        if (!auth.token) return {valid: false, code: 500, transient: false};
        startB2bProxySession(auth);
        return auth;
    } catch (err) {
        const transient = isTransientAxiosError(err);
        const code      = b2bErrorCode(err, transient);

        if (code === B2B_ERROR_TOKEN_EXPIRED && !afterRefresh) {
            const refresh = await dispatch('refreshB2bToken');
            if (refresh.success) return authenticateB2bAgain({dispatch, rootGetters}, true);
            return {valid: false, code: refresh.code, transient: !!refresh.transient};
        }
        return {valid: false, code, transient};
    }
}

const state = {
    authenticated: false,
    id: null,
    login: null,
    token: null,
    type: null,
    cashier: null,
    terminal: null,
    profileType: null,
    bets: [],
    bets_in_progress: [],
    bets_all: [],
    bets_all_in_progress: [],
    bets_count_all: 0,
    bets_count_all_in_progress: 0,
    modal_idle: false,
    language: null,
    timezone: null,
    detail_my_bet: [],
    agencyNet: null,
    agencyLogin: null,
    operatorNet: null,
    operatorLogin: null,
    canPlay: true,
    userIdOfUserToPlayBetFor: null,
    loginOfUserToPlayBetFor: null,
    b2bToken: null
};

const getters = {

    getAuthenticated(state) {
        return state.authenticated
    },

    getId(state) {
        return state.id
    },

    getLogin(state) {
        return state.login
    },

    getToken(state) {
        return state.token
    },
    getType(state) {
        return state.type
    },
    getCashier(state) {
        return state.cashier
    },
    getTerminal(state) {
        return state.terminal
    },
    getProfileType(state) {
        return state.profileType
    },

    getMyBets(state) {
        return state.bets
    },

    getMyBetsInProgress(state) {
        return state.bets_in_progress
    },

    getMyBetsAll(state) {
        return state.bets_all
    },

    getMyBetsInProgressAll(state) {
        return state.bets_all_in_progress
    },

    getDetailMyBets(state) {
        return state.detail_my_bet
    },

    getMyBetCounts(state, getters) {
        return state.bets_count_all;
    },

    getModalIdle(state) {
        return state.modal_idle
    },

    getLanguage(state) {
        return state.language
    },

    getTimezone(state) {
        return state.timezone
    },

    getAgencyLogin(state) {
        return state.agencyLogin
    },

    getAgencyNet(state) {
        return state.agencyNet
    },

    getOperatorLogin(state) {
        return state.operatorLogin
    },

    getOperatorNet(state) {
        return state.operatorNet
    },

    getCanPlay(state) {
        return state.canPlay
    },

    getUserIdOfUserToPlayBetFor(state) {
        return state.userIdOfUserToPlayBetFor
    },

    getLoginOfUserToPlayBetFor(state) {
        return state.loginOfUserToPlayBetFor
    },

    getB2bToken(state) {
        return state.b2bToken
    }
};

const actions = {

    async setUserData(context, payload) {
        context.commit('SET_USER_DATA', payload);
    },

    async setModalIdle(context, payload) {

        context.commit('SET_MODAL_IDLE', payload);
    },

    async setDetailMyBet(context, payload) {
        context.commit('SET_DETAIL_MY_BETS', payload);
    },

    async fetchMyBets(context, payload) {

        const all = (payload.all != undefined && payload.all != undefined) ? payload.all : false;

        const showOnlyMyBets = payload.showOnlyMyBets != null ? payload.showOnlyMyBets : null;
        let dontShowCancelled = true;
        let url = `${context.rootGetters['settings/getProxyUrl']}${proxyPath(context.rootGetters, '/bet/v3/get-my-bets')}`;

        const requestPayload = {
            id_user: context.rootGetters['user/getId'],
            all: all,
            dontShowCancelled: dontShowCancelled
        }

        if (showOnlyMyBets != null) {
            requestPayload.onlyPlayedByMe = showOnlyMyBets
        }

        return await axios.post(`${url}`, proxyBody(context.rootGetters, requestPayload), {headers: proxyHeaders(context.rootGetters, getProxyAccessToken())})
            .then(async (res) => {

                // Guarded: an unexpected body (an error envelope, a 200 with a
                // different shape) used to throw on .filter INSIDE this .then,
                // which the catch below swallowed — so SET_MY_BETS never ran and
                // the list silently kept whatever it held before, with only a
                // console line to say so. An empty list is the honest answer.
                const payloadBets = res && res.data && Array.isArray(res.data.data)
                    ? res.data.data
                    : [];

                context.commit('SET_MY_BETS', payloadBets.filter(el => el && el.result != 'cancelled'));

            }).catch((error) => {
                console.error('[user/fetchMyBets]', error);
            });

    },

    async fetchMyBetsInProgress(context, payload) {

        var all = (payload.all != undefined && payload.all != undefined) ? payload.all : false
        var in_progress = true

        let url = `${context.rootGetters['settings/getProxyUrl']}${proxyPath(context.rootGetters, '/bet/v3/get-my-bets')}`;
        var data = await axios.post(`${url}`, proxyBody(context.rootGetters, {
            'id_user': context.rootGetters['user/getId'],
            'all': all,
            'in_progress': in_progress
        }), {headers: proxyHeaders(context.rootGetters, getProxyAccessToken())});
        await context.commit('SET_MY_BETS_IN_PROGRESS', data.data.data);

        return data.data.data;
    },

    async fetchMyBetsAll(context, payload) {

        var all = (payload.all != undefined && payload.all != undefined) ? payload.all : false

        let url = `${context.rootGetters['settings/getProxyUrl']}${proxyPath(context.rootGetters, '/bet/v3/get-my-bets')}`;
        return await axios.post(`${url}`, proxyBody(context.rootGetters, {
            'id_user': context.rootGetters['user/getId'],
            'all': all
        }), {headers: proxyHeaders(context.rootGetters, getProxyAccessToken())})
            .then(async (res) => {
                context.commit('SET_MY_BETS_ALL', res.data.data);
            }).catch((error) => {
                console.log(error);
            });

    },

    async fetchMyBetsInProgressAll(context, payload) {

        var all = (payload.all != undefined && payload.all != undefined) ? payload.all : false

        let url = `${context.rootGetters['settings/getProxyUrl']}${proxyPath(context.rootGetters, '/bet/v3/get-my-bets')}`;
        return await axios.post(`${url}`, proxyBody(context.rootGetters, {
            'id_user': context.rootGetters['user/getId'],
            'all': all
        }), {headers: proxyHeaders(context.rootGetters, getProxyAccessToken())})
            .then(async (res) => {
                context.commit('SET_MY_BETS_ALL', res.data.data);
            }).catch((error) => {
                console.log(error);
            });
    },

    async fetchMyBetsAllFilter(context, payload) {

        let url = `${context.rootGetters['settings/getProxyUrl']}${proxyPath(context.rootGetters, '/bet/v3/get-my-bets-filter')}`;
        return await axios.post(`${url}`, proxyBody(context.rootGetters, {'id_user': context.rootGetters['user/getId'], ...payload}), {headers: proxyHeaders(context.rootGetters, getProxyAccessToken())})
            .then(async (res) => {
                context.commit('SET_MY_BETS_ALL', res.data.data);
                context.commit('SET_MY_BETS_COUNT', res.data.counts);
            }).catch((error) => {
                console.log(error);
            });
    },

    async fetchMyBetsAllWithoutCancelled(context, payload) {

        if (payload.result == null) {
            payload.dontShowCancelled = true;
        }
        let url = `${context.rootGetters['settings/getProxyUrl']}${proxyPath(context.rootGetters, '/bet/v3/get-my-bets-filter')}`;
        return await axios.post(`${url}`, proxyBody(context.rootGetters, {'id_user': context.rootGetters['user/getId'], ...payload}), {headers: proxyHeaders(context.rootGetters, getProxyAccessToken())})
            .then(async (res) => {

                context.commit('SET_MY_BETS_ALL', res.data.data);
                context.commit('SET_MY_BETS_COUNT', res.data.counts);

            }).catch((error) => {
                console.log(error);
            });
    },
    async fetchMyBetsInProgressAllFilter(context, payload) {
        let url = `${context.rootGetters['settings/getProxyUrl']}${proxyPath(context.rootGetters, '/bet/v3/get-my-bets-filter')}`;
        return await axios.post(`${url}`, proxyBody(context.rootGetters, {'id_user': context.rootGetters['user/getId'], ...payload}), {headers: proxyHeaders(context.rootGetters, getProxyAccessToken())})
            .then(async (res) => {
                context.commit('SET_MY_BETS_ALL_IN_PROGRESS', res.data.data);
                context.commit('SET_MY_BETS_COUNT_IN_PROGRESS', res.data.counts);
            }).catch((error) => {
                console.log(error);
            });
    },

    async removeUserData(context, payload) {
        context.commit('REMOVE_USER_DATA');
    },

    async setCanPlay(context, payload) {
        context.commit('SET_CAN_PLAY', payload);
    },

    async setUserIdOfUserToPlayBetFor(context, payload) {
        context.commit('SET_USER_ID_OF_USER_TO_PLAY_BET_FOR', payload);
    },

    async setLoginOfUserToPlayBetFor(context, payload) {
        context.commit('SET_LOGIN_OF_USER_TO_PLAY_BET_FOR', payload);
    },
    async fetchUserBalance(context, payload) {

        const customerId = payload.customerId;
        const userId = payload.userId;
        const proxyUrl = context.rootGetters['settings/getProxyUrl'];

        if (proxyUrl == null) {
            return null
        }

        let url = `${context.rootGetters['settings/getProxyUrl']}/api/wallet/customers/${customerId}/users/${userId}/walletGroupBalances`;

        return await axios({
            method: 'get',
            url: url,
            headers: {
                'content-type': 'application/json',
                'Authorization': `Bearer ${getProxyAccessToken()}`
            },
        })
    },
    async fetchUserSportBalance(context, payload) {
        const customerId = payload.customerId;
        const userId = payload.userId;
        const proxyUrl = context.rootGetters['settings/getProxyUrl'];

        let url;
        let headers = {
            'content-type': 'application/json',
        };

        if (proxyUrl == null) {
            let urlBase = context.rootGetters['settings/getPlatformSettings'];
            url = urlBase.sircms_endpoint + `bookie/wallet/user/balances/users/${userId}/sport-bet-withdrawable-balance/`;
        } else {
            url = `${proxyUrl}/api/wallet/customers/${customerId}/users/${userId}/transactiontypes/SPORT_BET/withdrawablebalances`;
            headers['Authorization'] = `Bearer ${getProxyAccessToken()}`;
        }
        return await axios({
            method: 'get',
            url: url,
            headers: headers
        })
    },
    async getUserSportBalance(context, payload) {

        const apiResponse = await context.dispatch('fetchUserSportBalance', payload);

        let totalBalance = 0.00;

        if (apiResponse != null) {
            let wallets = apiResponse.data != null ? apiResponse.data : null;

            if (wallets != null && wallets.length > 0) {
                wallets.forEach(function (item) {
                    totalBalance += item.balance;
                });
            }
        }
        return totalBalance;
    },

    async setB2bToken(context, payload) {
        context.commit('SET_B2B_TOKEN', payload);
    },

    // Periodic B2B session check (the watcher, every minute). Returns:
    //   - a payload on success
    //   - { valid: false, code, transient } on failure. `transient: true` means
    //     the caller should retry (network/timeout/5xx); the watcher uses it to
    //     avoid logging users out on a flaky connection.
    // The proxy token is validated as the skins do (auth/validation, cheap). Every
    // B2B_REAUTH_INTERVAL_MS, or as soon as the proxy rejects the token, the partner's
    // token is checked again with a new /b2b/authenticate (reauthenticateB2b): a session
    // closed on the partner side is noticed within that interval. A token rejected
    // B2B_MAX_REJECTED_TOKENS times in a row ends the session instead of re-authenticating.
    async checkB2bToken({dispatch, rootGetters}) {
        if (!rootGetters['user/getB2bToken']) return {valid: false, code: B2B_ERROR_NO_TOKEN, transient: false};
        if (isB2bReauthDue(B2B_REAUTH_INTERVAL_MS)) return dispatch('reauthenticateB2b');

        try {
            const data = await b2bValidateSession({
                proxyUrl:     rootGetters['settings/getProxyUrl'],
                accessToken:  getB2bProxyAccessToken(),
                refreshToken: getB2bProxyRefreshToken()
            });
            if (data && data.success === true) {
                noteB2bProxyTokenAccepted();
                if (data.refresh === true && data.userToken) {
                    updateB2bProxyTokens({token: data.userToken.token, refreshToken: data.userToken.refresh_token});
                }
                return data;
            }
            return dispatch('reauthenticateB2b', {tokenRejected: true});
        } catch (err) {
            if (err?.response?.status === 401) return dispatch('reauthenticateB2b', {tokenRejected: true});
            const transient = isTransientAxiosError(err);
            return {valid: false, code: err?.response?.status ?? 0, transient};
        }
    },

    // A new /b2b/authenticate with the partner's token: a fresh proxy session, or the partner's verdict.
    // `tokenRejected`: the proxy has just rejected the current token; after B2B_MAX_REJECTED_TOKENS in a
    // row the session ends here, without calling the proxy again. One re-authentication at a time.
    async reauthenticateB2b({dispatch, rootGetters}, {tokenRejected = false} = {}) {
        if (b2bReauthInFlight) return b2bReauthInFlight;

        if (tokenRejected) {
            if (getB2bProxyTokenRejections() >= B2B_MAX_REJECTED_TOKENS) {
                console.error('[B2B] The proxy keeps rejecting fresh tokens: session ended, no new authenticate');
                return {valid: false, code: 401, transient: false};
            }
            noteB2bProxyTokenRejected();
        }

        b2bReauthInFlight = authenticateB2bAgain({dispatch, rootGetters}, false);
        try {
            return await b2bReauthInFlight;
        } finally {
            b2bReauthInFlight = null;
        }
    },

    async refreshB2bToken({commit, rootGetters}) {
        const oldToken = rootGetters['user/getB2bToken'];
        if (!oldToken) return {success: false, code: B2B_ERROR_NO_TOKEN, transient: false};

        try {
            const data = await b2bRefreshToken({
                operatorToken: oldToken,
                customerId:    rootGetters['settings/getCustomerId'],
                platformId:    rootGetters['settings/getPlatformId']
            });
            const newToken = data?.token ?? null;
            if (!newToken) return {success: false, code: 500, transient: false};

            commit('SET_B2B_TOKEN', newToken);
            return {success: true, token: newToken};
        } catch (err) {
            const transient = isTransientAxiosError(err);
            const code      = err?.response?.data?.code ?? (transient ? 0 : 500);
            console.error('[B2B] Refresh token failed', err);
            return {success: false, code, transient};
        }
    },

    async depositTransfer(context, payload) {
        const amount = payload.amount;
        const customerId = payload.customerId;
        const receiverIdUser = payload.receiverIdUser;
        const receiverLogin = payload.receiverLogin;
        const proxyUrl = context.rootGetters['settings/getProxyUrl'];

        let url;
        let headers = {
            'content-type': 'application/json',
        };

        let data_form = {
            customer: context.rootGetters['settings/getCustomerId'].toString(),
            amount: amount,
            reciever: receiverIdUser.toString(),
            reference: context.rootGetters['user/getId'].toString(),
            login: context.rootGetters['user/getLogin'].toString(),
            nameUser: receiverLogin,
            passCode: "123456", // todo ... this is a dirty fix remove me when server side ready
            eventDescription: "Deposit"
        };


        if (proxyUrl == null) {
            let urlBase = context.rootGetters['settings/getPlatformSettings'];
            url = urlBase.sircms_endpoint + `bookie/rexcash/self-transfer/self/transfer/deposit`;
        } else {
            url = `${proxyUrl}/rexcash/self-transfer/self/transfer/deposit`;
            headers['Authorization'] = `Bearer ${getProxyAccessToken()}`;
        }

        return await axios({
            method: 'post',
            url: url,
            headers: headers,
            data: data_form
        });
    }
};

const mutations = {
    SET_USER_TOKEN(state, token) {
        state.token = token;
    },

    SET_USER_DATA(state, payload) {
        state.authenticated = true;
        state.id = payload.id;
        state.login = payload.login;
        state.token = payload.token;
        state.type = payload.type;
        state.cashier = payload.cashier;
        state.terminal = payload.terminal;
        state.profileType = payload.profileType ?? null;
        state.language = payload.language;
        state.timezone = payload.timezone;
        state.agencyNet = payload.agencyNet;
        state.agencyLogin = payload.agencyLogin;
        state.operatorNet = payload.operatorNet;
        state.operatorLogin = payload.operatorLogin;
    },

    REMOVE_USER_DATA(state) {
        state.authenticated = false;
        state.id = null;
        state.login = null;
        state.token = null;
        state.type = null;
        state.cashier = null;
        state.terminal = null;
        state.profileType = null;
        state.language = null;
        state.timezone = null;
        state.agencyNet = null;
        state.agencyLogin = null;
        state.operatorNet = null;
        state.operatorLogin = null;
        state.canPlay = true;
        state.userIdOfUserToPlayBetFor = null;
        state.loginOfUserToPlayBetFor = null;
        state.b2bToken = null;
    },

    SET_MY_BETS(state, payload) {
        state.bets = payload
    },
    SET_MY_BETS_IN_PROGRESS(state, payload) {
        state.bets_in_progress = payload
    },
    SET_MY_BETS_ALL(state, payload) {
        state.bets_all = payload
    },
    SET_MY_BETS_ALL_IN_PROGRESS(state, payload) {
        state.bets_all_in_progress = payload
    },
    SET_MY_BETS_COUNT(state, payload) {
        state.bets_count_all = payload
    },
    SET_MY_BETS_COUNT_IN_PROGRESS(state, payload) {
        state.bets_count_all_in_progress = payload
    },
    SET_MODAL_IDLE(state, payload) {
        state.modal_idle = payload
    },
    SET_DETAIL_MY_BETS(state, payload) {
        state.detail_my_bet = payload
    },
    SET_CAN_PLAY(state, payload) {
        state.canPlay = payload
    },
    SET_USER_ID_OF_USER_TO_PLAY_BET_FOR(state, payload) {
        state.userIdOfUserToPlayBetFor = payload
    },
    SET_LOGIN_OF_USER_TO_PLAY_BET_FOR(state, payload) {
        state.loginOfUserToPlayBetFor = payload
    },
    SET_B2B_TOKEN(state, payload) {
        state.b2bToken = payload;
    }

};

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions
}
